import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Shield, MessageCircle, Video, BookOpen, Wind, Users, CheckCircle, Handshake, Sparkles, TrendingUp, MapPin, Lock, Check, GraduationCap, Briefcase, Crown } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import EditableText from "../components/EditableText";
import PreviewModeToggle from "../components/PreviewModeToggle";
import { useBrand } from "../providers/BootstrapProvider";
import FoundingSpotsCounter from "../components/FoundingSpotsCounter";
import PageBlocks from "../components/PageBlocks";

const LandingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const brand = useBrand();
  const [showPartnerForm, setShowPartnerForm] = useState(false);
  const [partnerSubmitting, setPartnerSubmitting] = useState(false);
  const [stats, setStats] = useState(null);
  const [plansData, setPlansData] = useState(null);
  const [interval, setInterval] = useState("monthly");
  const [partnerForm, setPartnerForm] = useState({
    business_name: "", contact_name: "", email: "", phone: "",
    website: "", specialty: "financial_advisor", location: "",
    province: "AB", description: "", member_discount: ""
  });

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      axios.get(`${API}/public/landing-stats`).catch(() => null),
      axios.get(`${API}/payments/plans`).catch(() => null),
    ]).then(([statsRes, plansRes]) => {
      if (cancelled) return;
      if (statsRes) setStats(statsRes.data);
      if (plansRes) setPlansData(plansRes.data);
    });
    return () => { cancelled = true; };
  }, []);

  const handlePartnerApply = async (e) => {
    e.preventDefault();
    if (!partnerForm.business_name || !partnerForm.contact_name || !partnerForm.email || !partnerForm.description) {
      toast.error("Please fill in all required fields"); return;
    }
    setPartnerSubmitting(true);
    try {
      await axios.post(`${API}/partners/apply`, partnerForm);
      toast.success("Application submitted! We'll be in touch.");
      setShowPartnerForm(false);
      setPartnerForm({ business_name: "", contact_name: "", email: "", phone: "", website: "", specialty: "financial_advisor", location: "", province: "AB", description: "", member_discount: "" });
    } catch (error) { toast.error("Failed to submit. Please try again."); }
    finally { setPartnerSubmitting(false); }
  };

  // NOTE: We deliberately do NOT auto-navigate logged-in users to /dashboard here.
  // RootLanding is the single source of truth for what to render on "/" — during
  // prelaunch it returns <PrelaunchLanding/>, post-launch it returns <LandingPage/>.
  // A redirect here would cause logged-in admins to ping-pong / → /dashboard even
  // during prelaunch (because of render races between context providers). Logged-in
  // users seeing the marketing landing on / is fine; they can click "Sign in" or
  // navigate to /dashboard themselves.

  const features = [
    { icon: Shield, title: "Verified Community", desc: "Connect with registered dental hygienists verified through Canadian provincial registries" },
    { icon: MessageCircle, title: "Discussions & DMs", desc: "Share knowledge, ask questions, and privately message fellow professionals" },
    { icon: Video, title: "Live Video Sessions", desc: "Join continuing education courses, seminars, and information sessions" },
    { icon: BookOpen, title: "Resources Library", desc: "Access curated educational materials, articles, and professional resources" },
    { icon: Wind, title: "Wellness Tools", desc: "Take a break with guided box breathing and decompression exercises" },
    { icon: Users, title: "Member Directory", desc: "Find and connect with specialists, office owners, and therapists" },
  ];

  return (
    <div className="min-h-screen bg-[#FAF7F2]">
      <PageBlocks pageKey="launch" placement="top" className="max-w-4xl mx-auto px-6 pt-6" />
      {/* Header */}
      <header className="backdrop-blur-xl bg-white/80 border-b border-[#E5DFEC]/50 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <img src={brand.logo_url} alt="DHDH" className="w-10 h-10 sm:w-11 sm:h-11 rounded-full object-cover shrink-0" />
            {/* Show full name on tablet+, abbreviated on phones */}
            <span className="hidden md:inline font-semibold text-lg text-[#6B4E8A] truncate" style={{ fontFamily: 'Outfit, sans-serif' }}>Dental Hygiene Development Hub</span>
            <span className="md:hidden font-semibold text-base text-[#6B4E8A]" style={{ fontFamily: 'Outfit, sans-serif' }}>DHDH</span>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-3 shrink-0">
            <Button
              variant="ghost"
              className="text-[#6B4E8A] hover:bg-[#F0ECF7] text-sm px-3 sm:px-4"
              onClick={() => navigate("/login")}
              data-testid="login-btn"
            >
              Sign In
            </Button>
            <Button
              className="bg-[#6B4E8A] text-white hover:bg-[#553D6F] rounded-full px-4 sm:px-6 text-sm whitespace-nowrap"
              onClick={() => navigate("/register")}
              data-testid="register-btn"
            >
              Get Started
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 py-20 lg:py-32">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-4xl mx-auto"
        >
          <EditableText
            blockKey="launch.hero_eyebrow"
            fallback="For Canadian Dental Hygienists"
            as="p"
            className="text-xs font-bold uppercase tracking-[0.2em] text-[#8DB5A0] mb-6"
          />
          <EditableText
            blockKey="launch.hero_headline"
            fallback="Your Professional Community Awaits"
            as="h1"
            className="text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-[#6B4E8A] mb-6"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          />
          <EditableText
            blockKey="launch.hero_subhead"
            fallback="Connect with verified dental hygienists across Canada. Share knowledge, attend continuing education sessions, and grow together as professionals."
            as="p"
            className="text-lg text-[#475569] leading-relaxed mb-4 max-w-2xl mx-auto"
            multiline
          />
          <p className="text-sm text-[#64748B] mb-10 max-w-xl mx-auto">
            Starting from <strong className="text-[#8DB5A0]">$5/month</strong> for students · <strong className="text-[#8DB5A0]">first month free</strong> on every tier.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
            <Button 
              className="bg-[#6B4E8A] text-white hover:bg-[#553D6F] rounded-full px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg w-full sm:w-auto"
              onClick={() => navigate("/register")}
              data-testid="hero-get-started-btn"
            >
              Start Your Free Trial
            </Button>
            <Button 
              variant="outline"
              className="border-[#6B4E8A] text-[#6B4E8A] hover:bg-[#FAF7F2] rounded-full px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg w-full sm:w-auto"
              onClick={() => {
                const el = document.getElementById("pricing");
                if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
              data-testid="hero-pricing-btn"
            >
              See Pricing
            </Button>
          </div>
        </motion.div>

        {/* Trust bar — live anonymized stats */}
        {stats && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-16 grid grid-cols-3 gap-4 sm:gap-8 max-w-3xl mx-auto"
            data-testid="landing-trust-bar"
          >
            <div className="text-center">
              <div className="text-3xl sm:text-4xl font-light text-[#6B4E8A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {stats.members.verified_count_rounded || stats.members.verified_count_exact}
                {stats.members.verified_count_rounded > 0 && stats.members.verified_count_rounded < stats.members.verified_count_exact ? "+" : ""}
              </div>
              <p className="text-xs uppercase tracking-wider text-[#64748B] mt-2">Verified Hygienists</p>
            </div>
            <div className="text-center border-x border-[#E5DFEC]">
              <div className="text-3xl sm:text-4xl font-light text-[#6B4E8A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {stats.members.provinces_covered}
              </div>
              <p className="text-xs uppercase tracking-wider text-[#64748B] mt-2">Provinces Covered</p>
            </div>
            <div className="text-center">
              <div className="text-3xl sm:text-4xl font-light text-[#6B4E8A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {stats.activity.discussions_total_rounded || stats.activity.discussions_30d}
                {stats.activity.discussions_total_rounded > 0 ? "+" : ""}
              </div>
              <p className="text-xs uppercase tracking-wider text-[#64748B] mt-2">Discussions</p>
            </div>
          </motion.div>
        )}

        {/* Founding-Member Spots Counter — appears near top of page, above pricing */}
        <FoundingSpotsCounter variant="hero" className="mt-12 max-w-2xl mx-auto" />
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <motion.div 
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <EditableText
            blockKey="launch.features_title"
            fallback="Everything You Need"
            as="h2"
            className="text-2xl sm:text-3xl lg:text-4xl font-medium tracking-tight text-[#6B4E8A] mb-4"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          />
          <EditableText
            blockKey="launch.features_subtitle"
            fallback="A complete platform designed specifically for dental hygienists"
            as="p"
            className="text-[#64748B] max-w-xl mx-auto"
            multiline
          />
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, idx) => (
            <motion.div 
              key={feature.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: idx * 0.1 }}
              viewport={{ once: true }}
              className="bg-white border border-[#E5DFEC] rounded-2xl p-6 card-hover"
            >
              <div className="w-12 h-12 rounded-xl bg-[#F0ECF7] flex items-center justify-center mb-4">
                <feature.icon className="w-6 h-6 text-[#8DB5A0]" />
              </div>
              <h3 className="text-xl font-medium text-[#6B4E8A] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {feature.title}
              </h3>
              <p className="text-[#64748B] text-sm leading-relaxed">
                {feature.desc}
              </p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Topic of the Month + Wage Preview — social proof */}
      {(stats?.topic_of_month?.title || stats?.wage_preview?.length > 0) && (
        <section className="max-w-7xl mx-auto px-6 py-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Topic of the Month teaser */}
            {stats?.topic_of_month?.title && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-[#6B4E8A] to-[#553D6F] rounded-3xl p-8 lg:p-10 text-white relative overflow-hidden"
                data-testid="landing-totm-card"
              >
                <div className="absolute -top-12 -right-12 w-40 h-40 bg-[#8DB5A0]/10 rounded-full blur-3xl" />
                <div className="relative">
                  <div className="flex items-center gap-2 mb-4">
                    <Sparkles className="w-4 h-4 text-[#8DB5A0]" />
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8DB5A0]">Topic of the Month</p>
                  </div>
                  <h3 className="text-2xl sm:text-3xl font-medium text-white mb-3 leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {stats.topic_of_month.title}
                  </h3>
                  {stats.topic_of_month.description && (
                    <p className="text-[#94A3B8] leading-relaxed text-sm mb-6 line-clamp-3">
                      {stats.topic_of_month.description}
                    </p>
                  )}
                  <div className="flex items-center gap-3 pt-4 border-t border-white/10">
                    <Lock className="w-4 h-4 text-[#94A3B8]" />
                    <p className="text-sm text-[#94A3B8]">
                      Join the discussion — verified members only
                    </p>
                  </div>
                  <Button
                    className="mt-5 bg-[#8DB5A0] text-[#6B4E8A] hover:bg-[#C49E2D] rounded-full px-6"
                    onClick={() => navigate("/register")}
                    data-testid="totm-join-btn"
                  >
                    Join to read the thread
                  </Button>
                </div>
              </motion.div>
            )}

            {/* Wage Transparency preview */}
            {stats?.wage_preview?.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                viewport={{ once: true }}
                className="bg-white border border-[#E5DFEC] rounded-3xl p-8 lg:p-10"
                data-testid="landing-wages-card"
              >
                <div className="flex items-center gap-2 mb-4">
                  <TrendingUp className="w-4 h-4 text-[#8DB5A0]" />
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8DB5A0]">Wage Transparency</p>
                </div>
                <h3 className="text-2xl sm:text-3xl font-medium text-[#6B4E8A] mb-2 leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Anonymous, member-submitted hourly rates
                </h3>
                <p className="text-[#64748B] text-sm mb-6">
                  See what hygienists across Canada actually earn — no surveys, no guesswork.
                </p>
                <div className="space-y-3">
                  {stats.wage_preview.slice(0, 4).map((row) => {
                    const max = Math.max(...stats.wage_preview.map(r => r.avg_hourly_cad));
                    const pct = Math.max(20, Math.round((row.avg_hourly_cad / max) * 100));
                    return (
                      <div key={row.province_code} className="flex items-center gap-3">
                        <div className="w-12 shrink-0">
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-[#6B4E8A]">
                            <MapPin className="w-3 h-3 text-[#94A3B8]" />
                            {row.province_code}
                          </span>
                        </div>
                        <div className="flex-1 h-7 bg-[#F0ECF7] rounded-full overflow-hidden relative">
                          <div
                            className="h-full bg-gradient-to-r from-[#8DB5A0] to-[#8DB5A0] rounded-full flex items-center justify-end pr-3 transition-all"
                            style={{ width: `${pct}%` }}
                          >
                            <span className="text-xs font-medium text-white">${row.avg_hourly_cad.toFixed(2)}/hr</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="flex items-center gap-3 mt-6 pt-4 border-t border-[#E5DFEC]">
                  <Lock className="w-4 h-4 text-[#94A3B8]" />
                  <p className="text-sm text-[#64748B]">
                    Full breakdown by experience, role & province inside.
                  </p>
                </div>
                <Button
                  className="mt-5 bg-[#6B4E8A] text-white hover:bg-[#553D6F] rounded-full px-6"
                  onClick={() => navigate("/register")}
                  data-testid="wages-unlock-btn"
                >
                  Unlock full data
                </Button>
              </motion.div>
            )}
          </div>
        </section>
      )}

      {/* Pricing Section — public, all 4 tiers */}
      {plansData && (
        <section className="max-w-7xl mx-auto px-6 py-20" id="pricing" data-testid="landing-pricing-section">
          <div className="text-center mb-12">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8DB5A0] mb-3">Membership</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-light text-[#6B4E8A] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Pick your tier
            </h2>
            <p className="text-[#64748B] max-w-xl mx-auto mb-8">
              Every tier starts with a <strong className="text-[#6B4E8A]">first month free</strong>. Cancel any time.
            </p>
            <div className="inline-flex bg-white border border-[#E5DFEC] rounded-full p-1" data-testid="landing-interval-toggle">
              {["monthly", "annual"].map(opt => (
                <button
                  key={opt}
                  onClick={() => setInterval(opt)}
                  className={`px-4 sm:px-5 py-2 rounded-full text-xs sm:text-sm font-medium transition-all whitespace-nowrap ${interval === opt ? "bg-[#6B4E8A] text-white" : "text-[#64748B] hover:text-[#6B4E8A]"}`}
                  data-testid={`landing-interval-${opt}`}
                >
                  {opt === "monthly" ? "Monthly" : "Annual"}
                  {opt === "annual" && (
                    <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 bg-[#10B981]/15 text-[#059669] text-[9px] sm:text-[10px] uppercase tracking-wider rounded-full font-bold">
                      Save up to 28%
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Founding-Member Spots Counter — second placement near pricing */}
          <FoundingSpotsCounter variant="pricing" className="max-w-2xl mx-auto mb-8" />

          {/* Founding Pro banner */}
          {plansData.founding_pro?.active && (
            <div className="bg-[#6B4E8A] text-white rounded-2xl p-5 mb-10 flex items-center gap-4 max-w-3xl mx-auto" data-testid="landing-founding-banner">
              <div className="w-10 h-10 rounded-full bg-[#8DB5A0]/20 flex items-center justify-center shrink-0">
                <Sparkles className="w-5 h-5 text-[#8DB5A0]" />
              </div>
              <div className="flex-1 text-sm">
                <p className="font-medium text-[#8DB5A0]">
                  Founding Pro rate active — first {plansData.founding_pro.seats_total} subscribers only
                </p>
                <p className="text-white/70 text-xs">
                  ${plansData.founding_pro.monthly}/mo or ${plansData.founding_pro.annual}/yr · <strong className="text-[#8DB5A0]">locked for life</strong> · {plansData.founding_pro.seats_taken}/{plansData.founding_pro.seats_total} taken
                </p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
            {[
              { key: "student", icon: GraduationCap, accent: "#8B5CF6", features: ["Community & social board", "Topic of the month + resources", "Wage surveys + clinic ratings (read + submit)", "CE / mileage / shift trackers (PDF)", "Health & burnout check-in", "Dedicated student & new grad groups", "Preferred partners directory"], note: "Student ID or grad certificate required" },
              { key: "community", icon: Users, accent: "#6B4E8A", features: ["Community & social board", "Anonymous discussions + ToM", "Resources & event search", "Private messaging + meetups", "Wage surveys", "Clinic ratings (read only)", "Health & burnout check-in"] },
              { key: "hygienist", icon: Briefcase, accent: "#0EA5E9", badge: "Most popular", features: ["Everything in The Community", "Clinic ratings (read + submit)", "CE & mileage trackers (PDF)", "Shift tracker (full)", "Full note templates library", "Create & manage groups", "Job board access"] },
              { key: "pro", icon: Crown, accent: "#8DB5A0", badge: plansData.founding_pro?.active ? "Founding rate" : "Pro toolkit", features: ["Everything in The Hygienist", "Receipt & expense tracker (PDF)", "Pro-only groups (owners, leadership)", "Mentorship matching", "Future CE courses + live video", "Exclusive workshops + early access", "Founding Pro badge (limited)"] },
            ].map(({ key, icon: Icon, accent, badge, features, note }) => {
              const tier = plansData.tiers[key];
              if (!tier) return null;
              const isPro = key === "pro";
              const isFounding = isPro && plansData.founding_pro?.active;
              const monthly = isFounding ? plansData.founding_pro.monthly : tier.monthly;
              const annual = isFounding ? plansData.founding_pro.annual : tier.annual;
              const price = interval === "monthly" ? monthly : annual;
              const original = isFounding ? (interval === "monthly" ? tier.monthly : tier.annual) : null;
              const annualSavings = +(monthly * 12 - annual).toFixed(2);
              const annualSavingsPct = Math.round((annualSavings / (monthly * 12)) * 100);
              return (
                <motion.div
                  key={key}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4 }}
                  viewport={{ once: true }}
                  className={`relative rounded-3xl p-6 flex flex-col ${isPro ? "bg-gradient-to-br from-[#6B4E8A] to-[#553D6F] text-white" : "bg-white border border-[#E5DFEC] text-[#6B4E8A]"}`}
                  data-testid={`landing-tier-${key}`}
                >
                  {badge && (
                    <div className={`absolute -top-3 left-6 text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full ${isPro ? "bg-[#8DB5A0] text-[#6B4E8A]" : "bg-[#8DB5A0] text-white"}`}>
                      {badge}
                    </div>
                  )}
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-4 ${isPro ? "bg-[#8DB5A0]/15" : "bg-[#FAF7F2] border border-[#E5DFEC]"}`}>
                    <Icon className="w-5 h-5" style={{ color: accent }} />
                  </div>
                  <h3 className={`text-xl font-medium mb-1 ${isPro ? "text-white" : "text-[#6B4E8A]"}`} style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {tier.label}
                  </h3>
                  <p className={`text-xs mb-5 leading-relaxed min-h-[3em] ${isPro ? "text-white/60" : "text-[#64748B]"}`}>
                    {tier.tagline}
                  </p>
                  <div className="mb-1 flex items-baseline gap-2 flex-wrap">
                    <span className={`text-4xl font-bold ${isPro ? "text-white" : "text-[#6B4E8A]"}`}>${price}</span>
                    <span className={`text-sm ${isPro ? "text-white/60" : "text-[#64748B]"}`}>CAD/{interval === "monthly" ? "month" : "year"}</span>
                  </div>
                  {/* Savings line — visible on annual; visible faintly on monthly to advertise the deal */}
                  {interval === "annual" ? (
                    <p className="text-xs text-[#10B981] mb-1 font-medium">
                      Save ${annualSavings} ({annualSavingsPct}%) vs paying monthly
                    </p>
                  ) : (
                    <p className={`text-xs mb-1 ${isPro ? "text-white/50" : "text-[#94A3B8]"}`}>
                      Annual: ${annual}/yr — save ${annualSavings} ({annualSavingsPct}%)
                    </p>
                  )}
                  {original !== null && (
                    <p className="text-xs text-[#8DB5A0] mb-1">
                      Founding rate · normally ${original} — locked for life
                    </p>
                  )}
                  <p className={`text-xs mb-5 ${isPro ? "text-[#8DB5A0]" : "text-[#8DB5A0]"}`}>First month free</p>
                  <Button
                    onClick={() => navigate("/register")}
                    className={`rounded-full py-5 text-sm font-medium mb-5 ${isPro ? "bg-[#8DB5A0] text-[#6B4E8A] hover:bg-[#C49E2D]" : "bg-[#6B4E8A] text-white hover:bg-[#553D6F]"}`}
                    data-testid={`landing-trial-${key}-btn`}
                  >
                    Start your free trial
                  </Button>
                  <ul className="space-y-2 flex-1">
                    {features.map((f) => (
                      <li key={f} className={`text-xs flex items-start gap-2 leading-relaxed ${isPro ? "text-white/85" : "text-[#475569]"}`}>
                        <Check className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${isPro ? "text-[#8DB5A0]" : "text-[#10B981]"}`} />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  {note && (
                    <p className={`text-[10px] mt-4 italic ${isPro ? "text-white/50" : "text-[#94A3B8]"}`}>{note}</p>
                  )}
                </motion.div>
              );
            })}
          </div>

          <p className="text-center text-xs text-[#94A3B8] mt-10">
            Secure payment by Stripe · CAD · cancel anytime · provincial-tax receipts auto-emailed
          </p>
        </section>
      )}

      {/* Verification Section */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="bg-[#6B4E8A] rounded-3xl p-8 lg:p-16 text-center">
          <div className="w-16 h-16 rounded-2xl bg-[#8DB5A0] flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-[#6B4E8A]" />
          </div>
          <EditableText
            blockKey="launch.verified_title"
            fallback="Verified Professionals Only"
            as="h2"
            className="text-2xl sm:text-3xl lg:text-4xl font-medium text-white mb-4"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          />
          <EditableText
            blockKey="launch.verified_body"
            fallback="Our verification process ensures every member is a registered dental hygienist in Canada. Submit your credentials and get verified to access all community features."
            as="p"
            className="text-[#94A3B8] max-w-2xl mx-auto mb-8"
            multiline
          />
          <div className="flex flex-wrap justify-center gap-4 text-sm text-[#94A3B8]">
            <span className="px-4 py-2 bg-white/10 rounded-full">Alberta (CRDHA)</span>
            <span className="px-4 py-2 bg-white/10 rounded-full">British Columbia (CDHBC)</span>
            <span className="px-4 py-2 bg-white/10 rounded-full">Ontario (CDHO)</span>
            <span className="px-4 py-2 bg-white/10 rounded-full">+ All Provinces</span>
          </div>
        </div>
      </section>

      {/* Become a Partner */}
      <section className="max-w-7xl mx-auto px-6 py-20" id="partners">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="bg-gradient-to-br from-[#FAF7F2] to-white border border-[#E5DFEC] rounded-3xl p-8 lg:p-16 flex flex-col lg:flex-row items-center gap-12"
        >
          <div className="flex-1">
            <div className="w-14 h-14 rounded-2xl bg-[#8DB5A0]/10 flex items-center justify-center mb-6">
              <Handshake className="w-7 h-7 text-[#8DB5A0]" />
            </div>
            <EditableText
              blockKey="launch.partner_title"
              fallback="Become a Preferred Partner"
              as="h2"
              className="text-2xl sm:text-3xl font-medium text-[#6B4E8A] mb-4"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            />
            <EditableText
              blockKey="launch.partner_body"
              fallback="Are you a financial advisor, accountant, insurance specialist, or other professional who serves dental hygienists? Join our Preferred Partners directory and connect with Canada's dental hygiene community."
              as="p"
              className="text-[#64748B] leading-relaxed mb-4"
              multiline
            />
            <ul className="space-y-2 text-sm text-[#64748B] mb-6">
              <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-[#10B981]" />Reach verified dental hygienists across Canada</li>
              <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-[#10B981]" />Feature your member-exclusive discounts</li>
              <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-[#10B981]" />Build trust within a professional community</li>
            </ul>
            <Dialog open={showPartnerForm} onOpenChange={setShowPartnerForm}>
              <DialogTrigger asChild>
                <Button className="bg-[#8DB5A0] text-[#6B4E8A] hover:bg-[#C49E2D] rounded-full px-8 py-6 text-lg font-medium" data-testid="become-partner-btn">
                  Apply to Partner
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Partner Application</DialogTitle></DialogHeader>
                <form onSubmit={handlePartnerApply} className="space-y-4 mt-4">
                  <div><Label>Business Name *</Label><Input value={partnerForm.business_name} onChange={e => setPartnerForm({...partnerForm, business_name: e.target.value})} className="mt-2 bg-white border-[#E5DFEC]" data-testid="apply-business-name" /></div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label>Contact Name *</Label><Input value={partnerForm.contact_name} onChange={e => setPartnerForm({...partnerForm, contact_name: e.target.value})} className="mt-2 bg-white border-[#E5DFEC]" data-testid="apply-contact-name" /></div>
                    <div><Label>Email *</Label><Input type="email" value={partnerForm.email} onChange={e => setPartnerForm({...partnerForm, email: e.target.value})} className="mt-2 bg-white border-[#E5DFEC]" data-testid="apply-email" /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label>Phone</Label><Input value={partnerForm.phone} onChange={e => setPartnerForm({...partnerForm, phone: e.target.value})} className="mt-2 bg-white border-[#E5DFEC]" /></div>
                    <div><Label>Website</Label><Input value={partnerForm.website} onChange={e => setPartnerForm({...partnerForm, website: e.target.value})} placeholder="https://..." className="mt-2 bg-white border-[#E5DFEC]" /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label>Specialty</Label>
                      <Select value={partnerForm.specialty} onValueChange={v => setPartnerForm({...partnerForm, specialty: v})}>
                        <SelectTrigger className="mt-2 bg-white border-[#E5DFEC]"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="financial_advisor">Financial Advisor</SelectItem>
                          <SelectItem value="accountant">Accountant</SelectItem>
                          <SelectItem value="tax_specialist">Tax Specialist</SelectItem>
                          <SelectItem value="insurance">Insurance Specialist</SelectItem>
                          <SelectItem value="legal">Legal / Lawyer</SelectItem>
                          <SelectItem value="real_estate">Real Estate</SelectItem>
                          <SelectItem value="investment">Investment Advisor</SelectItem>
                          <SelectItem value="bookkeeper">Bookkeeper</SelectItem>
                          <SelectItem value="practice_consultant">Practice Consultant</SelectItem>
                          <SelectItem value="wellness">Wellness / Mental Health</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div><Label>Province</Label>
                      <Select value={partnerForm.province} onValueChange={v => setPartnerForm({...partnerForm, province: v})}>
                        <SelectTrigger className="mt-2 bg-white border-[#E5DFEC]"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="AB">Alberta</SelectItem>
                          <SelectItem value="BC">British Columbia</SelectItem>
                          <SelectItem value="ON">Ontario</SelectItem>
                          <SelectItem value="QC">Quebec</SelectItem>
                          <SelectItem value="MB">Manitoba</SelectItem>
                          <SelectItem value="SK">Saskatchewan</SelectItem>
                          <SelectItem value="NS">Nova Scotia</SelectItem>
                          <SelectItem value="NB">New Brunswick</SelectItem>
                          <SelectItem value="NL">Newfoundland</SelectItem>
                          <SelectItem value="PE">PEI</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div><Label>Location</Label><Input value={partnerForm.location} onChange={e => setPartnerForm({...partnerForm, location: e.target.value})} placeholder="Town / City" className="mt-2 bg-white border-[#E5DFEC]" /></div>
                  <div><Label>About Your Services *</Label><Textarea value={partnerForm.description} onChange={e => setPartnerForm({...partnerForm, description: e.target.value})} placeholder="Describe your services and how you help dental hygienists..." className="mt-2 bg-white border-[#E5DFEC] min-h-[100px]" data-testid="apply-description" /></div>
                  <div><Label>DHDH Member Discount (optional)</Label><Input value={partnerForm.member_discount} onChange={e => setPartnerForm({...partnerForm, member_discount: e.target.value})} placeholder="e.g. 15% off first consultation" className="mt-2 bg-white border-[#E5DFEC]" /></div>
                  <div className="flex justify-end gap-3 pt-2">
                    <Button type="button" variant="outline" onClick={() => setShowPartnerForm(false)} className="rounded-full">Cancel</Button>
                    <Button type="submit" className="bg-[#8DB5A0] text-[#6B4E8A] hover:bg-[#C49E2D] rounded-full" disabled={partnerSubmitting} data-testid="submit-partner-apply">{partnerSubmitting ? "Submitting..." : "Submit Application"}</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
          <div className="w-full lg:w-80 shrink-0 space-y-3">
            {["Financial Advisor", "Accountant", "Tax Specialist", "Insurance", "Legal", "Wellness"].map((s, i) => (
              <div key={s} className="flex items-center gap-3 bg-white border border-[#E5DFEC] rounded-xl p-3">
                <div className="w-8 h-8 rounded-lg bg-[#F0ECF7] flex items-center justify-center"><Handshake className="w-4 h-4 text-[#8DB5A0]" /></div>
                <span className="text-sm text-[#6B4E8A]">{s}</span>
              </div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#E5DFEC] py-8 mt-20">
        <div className="max-w-7xl mx-auto px-6 flex flex-col gap-4">
          <EditableText
            blockKey="launch.footer_disclaimer"
            fallback="DHDH is a peer community platform. Content shared by members does not constitute clinical, legal, or financial advice. Always consult a licensed professional."
            as="p"
            className="text-xs text-[#94A3B8] leading-relaxed text-center sm:text-left max-w-3xl"
            data-testid="global-platform-disclaimer"
            multiline
          />
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-[#64748B] text-sm">
            <p>© {new Date().getFullYear()} Dental Hygiene Development Hub. For registered dental hygienists in Canada.</p>
            <div className="flex gap-5 flex-wrap">
              <a href="/privacy" className="hover:text-[#6B4E8A] transition-colors" data-testid="footer-privacy-link">Privacy</a>
              <a href="/terms" className="hover:text-[#6B4E8A] transition-colors" data-testid="footer-terms-link">Terms</a>
              <a href="/cookie-policy" className="hover:text-[#6B4E8A] transition-colors" data-testid="footer-cookie-link">Cookie Policy</a>
              <a href="/disclaimer" className="hover:text-[#6B4E8A] transition-colors" data-testid="footer-disclaimer-link">Disclaimer</a>
              <a href="mailto:support@dhdh.ca" className="hover:text-[#6B4E8A] transition-colors" data-testid="footer-contact-link">Contact</a>
            </div>
          </div>
        </div>
      </footer>
      <PageBlocks pageKey="launch" placement="bottom" className="max-w-4xl mx-auto px-6 pb-10" />
      <PreviewModeToggle />
    </div>
  );
};

export default LandingPage;
